import type { HelpContent } from './types';

export const settingsGeneralHelp: HelpContent = {
  pageTitle: 'General Settings',
  description: 'General Settings is where administrators configure company information, financial calculation methods, dashboard targets, and the audit trail.',

  functionality: {
    overview:
      'This page contains four collapsible sections covering company identity, reporting calculations, dashboard benchmark targets, and audit logging. Changes saved here apply company-wide to all users and all projects.',
    keyFeatures: [
      {
        label: 'Company Details',
        detail: 'Store and update company name, address, bank details, and logo — used on invoices and throughout the platform.',
        requiredPermission: 'settings.generalSettings',
      },
      {
        label: 'GP Calculation Method',
        detail: 'Choose how Gross Profit and Gross Margin % are calculated across all reports and financial tabs.',
        requiredPermission: 'settings.generalSettings',
      },
      {
        label: 'Revenue Display in P&L Report',
        detail: 'Control whether the P&L Report shows Invoiced Revenue, Recognised Revenue, or both.',
        requiredPermission: 'settings.generalSettings',
      },
      {
        label: 'Dashboard Reference Lines',
        detail: 'Set firm-wide benchmark percentages used by Executive Dashboard widgets. Defaults apply when a field is left blank.',
        requiredPermission: 'settings.generalSettings',
      },
      {
        label: 'Utilisation Rate Formula',
        detail: 'Choose which formula is used to calculate utilisation across all projects and widgets.',
        requiredPermission: 'settings.generalSettings',
      },
      {
        label: 'Audit Trail',
        detail: 'Enable or disable the audit log and configure which event categories are recorded and how long entries are retained.',
        requiredPermission: 'settings.configureAuditTrail',
      },
    ],
    sectionsAndFields: [
      {
        name: 'Company Details',
        description: 'Accordion containing company name, registration number, VAT number, contact details, registered address, and bank details.',
        requiredPermission: 'settings.generalSettings',
      },
      {
        name: 'Company Logo',
        description: 'Upload or replace the company logo displayed on invoices. Supports PNG, JPG, SVG, WebP up to 10 MB.',
        requiredPermission: 'settings.generalSettings',
      },
      {
        name: 'GP Calculation Method',
        description: 'Radio selection between Simple, Contribution Margin, and Fully Loaded GP methods. Applies company-wide.',
        requiredPermission: 'settings.generalSettings',
      },
      {
        name: 'Revenue Display in P&L Report',
        description: 'Radio selection for which revenue rows appear in the P&L Report: Both, Invoiced only, or Recognised only.',
        requiredPermission: 'settings.generalSettings',
      },
      {
        name: 'Dashboard Reference Lines',
        description: 'Number inputs for Portfolio Target Margin %, Client Concentration Threshold %, and Target Utilisation % used as benchmarks on Executive Dashboard widgets. Each falls back to its default (25%, 30%, 75%) when blank.',
        requiredPermission: 'settings.generalSettings',
      },
      {
        name: 'Utilisation Rate Formula',
        description: 'Radio selection for the formula used to calculate utilisation: Actual vs Budget, Actual vs Allocated, or Billable vs Capacity.',
        requiredPermission: 'settings.generalSettings',
      },
      {
        name: 'Audit Logging',
        description: 'Toggle to enable or disable the master audit log and a dropdown to set the retention period.',
        requiredPermission: 'settings.configureAuditTrail',
      },
      {
        name: 'Audit Categories',
        description: 'Checkboxes to choose which event types are recorded: Project Changes, Financial Changes, Settings Changes, User & Permission Changes, and Billing & Invoicing.',
        requiredPermission: 'settings.configureAuditTrail',
      },
    ],
    useCases: [
      'An administrator uploads a new company logo before a round of client invoicing.',
      'A finance lead switches the GP calculation method from Simple to Contribution Margin to align with updated accounting standards.',
      'An administrator sets a portfolio target margin of 25% so the reference line appears on the Executive Dashboard chart.',
      'A compliance officer enables audit logging and sets a 5-year retention period to meet regulatory requirements.',
    ],
  },

  userGuide: {
    gettingStarted:
      'Open any accordion section by clicking its header. Make your changes and click Save at the bottom of the page. All changes apply immediately company-wide once saved.',
    steps: [
      {
        title: 'How do I upload or change the company logo?',
        requiredPermission: 'settings.generalSettings',
        items: [
          { text: 'Open the Company Details section by clicking its header.' },
          { text: 'If no logo exists, click the upload area or drag and drop an image file onto it.' },
          {
            text: 'If a logo already exists, click Replace Logo to choose a new file.',
            tip: 'Click Remove to delete the logo entirely.',
          },
          {
            text: 'The logo is automatically scaled to fit 400 × 120 px.',
            note: 'Maximum file size is 10 MB. Supported formats: PNG, JPG, SVG, WebP.',
          },
          { text: 'Click Save Settings at the bottom of the page to commit the change.' },
        ],
      },
      {
        title: 'How do I change the GP Calculation Method?',
        requiredPermission: 'settings.generalSettings',
        items: [
          { text: 'Scroll to the Reporting & Calculations section and expand it.' },
          { text: 'Click the GP Calculation Method accordion to expand it.' },
          {
            text: 'Select the method that matches your accounting approach: Simple, Contribution Margin, or Fully Loaded.',
            note: 'This setting changes how GP and GP % are calculated across all project Financials tabs, charts, and reports for every user.',
          },
          { text: 'Click Save Settings at the bottom of the page.' },
          {
            text: 'All reports and dashboards will reflect the new method immediately after saving.',
            tip: 'Use Reset to Defaults to revert to Simple GP if needed.',
          },
        ],
      },
      {
        title: 'How do I set dashboard reference lines?',
        requiredPermission: 'settings.generalSettings',
        items: [
          { text: 'Scroll to the Dashboard Targets section and expand it.' },
          { text: 'Enter a percentage value in the Portfolio Target Margin field to add a target line to the Portfolio Profitability chart.' },
          { text: 'Enter a value in Client Concentration Threshold to flag clients that exceed that share of total revenue.' },
          { text: 'Enter a value in Target Utilisation to add a reference line to utilisation charts.' },
          {
            text: 'Leave any field blank to hide the corresponding reference line.',
            tip: 'Click Clear next to a field to remove an existing value.',
          },
          { text: 'Click Save Settings at the bottom of the page.' },
        ],
      },
      {
        title: 'How do I enable the audit trail?',
        requiredPermission: 'settings.configureAuditTrail',
        items: [
          { text: 'Scroll to the Audit Trail section and expand it.' },
          {
            text: 'Tick the Enable Audit Logging checkbox.',
            note: 'When audit logging is off, no events are recorded regardless of the category settings below.',
          },
          { text: 'Select a Retention Period from the dropdown — choose Unlimited to keep all entries indefinitely.' },
          { text: 'In the Audit Categories section, tick the event types you want to record.' },
          { text: 'Click Save Settings at the bottom of the page.' },
        ],
      },
    ],
    proTips: [
      'If your company uses a finance integration (e.g. Xero or MYOB), Company Details fields will be synced automatically and become read-only here.',
      'The GP Calculation Method affects every report across the platform — communicate changes to your finance team before switching.',
      'Reference line values are optional. Adding them turns a plain chart into a benchmark view at no extra configuration cost.',
      'Audit log entries are tamper-evident. Once written they cannot be edited — only deleted according to the retention schedule.',
    ],
  },

  faq: [
    {
      question: 'Can I edit company details if a finance integration is connected?',
      answer:
        'No. When a finance system (such as Xero or MYOB) is connected, company details are synced from that system and are read-only in this page. Edit them in your finance system instead.',
    },
    {
      question: 'What does the GP Calculation Method change?',
      answer:
        'It changes how Gross Profit and Gross Margin % are calculated across all Financials tab cards, project charts, and reports. Simple uses Sales minus Direct Costs. Contribution Margin adds indirect variable costs. Fully Loaded includes all allocated overheads.',
    },
    {
      question: 'What does Revenue Display in P&L Report mean?',
      answer:
        'It controls which revenue rows appear in the Project P&L Report. Choose Both to see Invoiced Revenue and Recognised Revenue side by side, or select one to simplify the view for T&M or fixed-price businesses.',
    },
    {
      question: 'How do I remove a dashboard reference line?',
      answer: 'Clear the value from the relevant percentage field and save. A blank field hides the reference line.',
    },
    {
      question: 'What is the difference between the three utilisation formulas?',
      answer:
        'Actual vs Budgeted Hours measures budget consumption. Actual vs Allocated Hours measures how much scheduled capacity was used. Billable vs Available Capacity measures billable efficiency against total available time.',
    },
    {
      question: 'Can I configure the audit trail without enabling it?',
      answer:
        'Yes. You can set the retention period and audit categories while audit logging is turned off. Logging will only start recording once you enable the master toggle and save.',
      requiredPermission: 'settings.configureAuditTrail',
    },
    {
      question: 'What happens to audit log entries when the retention period expires?',
      answer:
        'Entries older than the selected retention period are automatically deleted. Set the period to Unlimited to keep all entries indefinitely.',
      requiredPermission: 'settings.configureAuditTrail',
    },
  ],

  relatedPages: [
    { label: 'Project Settings', path: '/settings/project' },
    { label: 'Integrations', path: '/settings/integrations' },
    { label: 'Roles & Permissions', path: '/settings/user-permissions' },
    { label: 'Notifications', path: '/settings/notifications' },
  ],
};
